import { buddyClient, qs } from './client';

/** Translations (H6): the chat UI's texts per language. Types follow buddy3's `contracts/src/admin.ts` and `translations.ts`. */

export interface TranslationEntry {
  /** Set once the language has its own text (numeric in buddy3); absent for a missing key. */
  id?: string | number;
  languageCode: string;
  namespace: string;
  key: string;
  text: string;
  /** English text shown while the language has none. */
  fallback?: string;
  missing?: boolean;
  updatedAt?: string;
}

export interface TranslationQuery {
  language?: string;
  namespace?: string;
  missingOnly?: boolean;
}

export type TranslationUpsert = Pick<TranslationEntry, 'languageCode' | 'namespace' | 'key' | 'text'>;

export interface SupportedLanguage {
  code: string;
  name: string;
  nyceLogicCodes?: string[];
}

/** Shown when buddy3 cannot list its languages. */
export const DEFAULT_LANGUAGES: SupportedLanguage[] = [
  { code: 'en', name: 'English' },
  { code: 'sv', name: 'Svenska' },
];

export const translationsFor = (target: string) => {
  const { get, send, download } = buddyClient(target);
  return {
    list: (q: TranslationQuery = {}) => get<TranslationEntry[]>(`/translations${qs({ ...q })}`),
    languages: () => get<SupportedLanguage[]>('/translations/languages'),
    upsert: (entry: TranslationUpsert) => send<TranslationEntry>('POST', '/translations', entry),
    remove: (id: string | number) => send<void>('DELETE', `/translations/${encodeURIComponent(String(id))}`),
    autoTranslate: (language: string, namespace?: string) =>
      send<{ translated: number; language: string }>('POST', '/translations/auto-translate', {
        language,
        namespace,
      }),
    exportFile: (language: string) =>
      download(`/translations/export${qs({ language })}`, `nycebuddy-translations-${language}.json`),
    /** The exported file's JSON (`{languageCode, translations: {namespace: {key: text}}}`). */
    importBundle: (bundle: unknown) => send<{ imported: number }>('POST', '/translations/import', bundle),
  };
};
